/**
 * Connect swimlane — the Devices panel: drag-to-reorder + show/hide.
 *
 * The layers-panel idiom (Figma/Photoshop): every device stays IN the list
 * whatever its visibility — a hidden device is greyed with a struck-through
 * eye, one click from coming back, so nothing is ever lost off-screen. The
 * trigger button wears the hidden-count, making hidden state visible from
 * the main view without opening anything.
 *
 * Reorder is SortableJS on a DOM list (the pattern plan-builder.ts proved;
 * drag verification recipe in memory `reference_sortablejs_chrome_drag_…`) —
 * the canvas renderer never learns about dragging, it just re-renders from
 * the saved order. State lives in the ADR-0027 preference doc via
 * profile.ts (`swimlane.byEoc.{eocId}`), per user per client.
 *
 * Styling is inline, following flags-editor.ts (the house pattern for
 * connect overlays — no stylesheet dependency).
 */
import Sortable from "sortablejs";

import {
  saveSwimlaneDevicePrefs,
  swimlaneDevicePrefs,
  type SwimlaneDevicePrefs,
} from "./profile";

export interface PanelDevice {
  deviceId: string;
  name: string;
  room: string;
  image: string | null;
}

/** Devices in the user's saved order: listed ids first, unlisted appended in
 *  natural order, unknown ids dropped. Shared by the panel and the view. */
export function orderDevices<T extends { deviceId: string }>(
  devices: T[],
  order: string[],
): T[] {
  if (order.length === 0) return devices;
  const byId = new Map(devices.map((d) => [d.deviceId, d]));
  const out: T[] = [];
  for (const id of order) {
    const d = byId.get(id);
    if (d) {
      out.push(d);
      byId.delete(id);
    }
  }
  out.push(...byId.values());
  return out;
}

/** Open the Devices panel anchored under `anchor`. `onChange` fires after
 *  every persisted change (reorder, hide, show, reset) — the caller
 *  re-renders the canvas and refreshes its badge. */
export function openDevicePanel(
  eocId: string,
  devices: PanelDevice[],
  anchor: HTMLElement,
  onChange: () => void,
): void {
  document.getElementById("connectDevicePanel")?.remove();

  const pop = document.createElement("div");
  pop.id = "connectDevicePanel";
  const r = anchor.getBoundingClientRect();
  Object.assign(pop.style, {
    position: "fixed",
    top: `${r.bottom + 6}px`,
    right: `${Math.max(8, window.innerWidth - r.right)}px`,
    zIndex: "60",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(17,24,39,.14)",
    width: "290px",
    maxHeight: "70vh",
    overflowY: "auto",
    padding: "8px",
    font: "13px system-ui, sans-serif",
  } satisfies Partial<CSSStyleDeclaration>);

  const head = document.createElement("div");
  Object.assign(head.style, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 4px 8px",
  } satisfies Partial<CSSStyleDeclaration>);
  head.innerHTML = `<strong style="font-size:13px;color:#111827">Devices</strong>`;
  const reset = document.createElement("button");
  reset.type = "button";
  reset.textContent = "Reset";
  Object.assign(reset.style, {
    border: "none",
    background: "none",
    color: "#6b7280",
    cursor: "pointer",
    fontSize: "12px",
  } satisfies Partial<CSSStyleDeclaration>);
  head.appendChild(reset);
  pop.appendChild(head);

  const list = document.createElement("div");
  pop.appendChild(list);

  const persist = (prefs: SwimlaneDevicePrefs) => {
    void saveSwimlaneDevicePrefs(eocId, prefs);
    onChange();
  };

  const rebuild = () => {
    const prefs = swimlaneDevicePrefs(eocId);
    const hidden = new Set(prefs.hidden);
    list.innerHTML = "";
    for (const d of orderDevices(devices, prefs.order)) {
      const isHidden = hidden.has(d.deviceId);
      const row = document.createElement("div");
      row.dataset.deviceId = d.deviceId;
      Object.assign(row.style, {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 4px",
        borderRadius: "6px",
        opacity: isHidden ? "0.45" : "1",
        background: "transparent",
      } satisfies Partial<CSSStyleDeclaration>);

      const grip = document.createElement("span");
      grip.className = "device-panel-grip";
      grip.textContent = "⠿";
      Object.assign(grip.style, {
        cursor: "grab",
        color: "#9ca3af",
        fontSize: "14px",
        userSelect: "none",
      } satisfies Partial<CSSStyleDeclaration>);
      row.appendChild(grip);

      if (d.image) {
        const img = document.createElement("img");
        img.src = d.image;
        Object.assign(img.style, {
          width: "26px",
          height: "26px",
          borderRadius: "5px",
          objectFit: "cover",
        } satisfies Partial<CSSStyleDeclaration>);
        row.appendChild(img);
      } else {
        const ph = document.createElement("span");
        ph.textContent = (d.name[0] ?? "?").toUpperCase();
        Object.assign(ph.style, {
          width: "26px",
          height: "26px",
          borderRadius: "5px",
          background: "#f3f4f6",
          color: "#9ca3af",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "700",
          flex: "none",
        } satisfies Partial<CSSStyleDeclaration>);
        row.appendChild(ph);
      }

      const text = document.createElement("div");
      Object.assign(text.style, { flex: "1", minWidth: "0" } satisfies Partial<CSSStyleDeclaration>);
      text.innerHTML = `
        <div style="font-weight:600;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(d.name)}</div>
        <div style="color:#6b7280;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(d.room)}</div>`;
      row.appendChild(text);

      const eye = document.createElement("button");
      eye.type = "button";
      eye.setAttribute("aria-pressed", String(!isHidden));
      eye.setAttribute(
        "aria-label",
        isHidden ? `Show ${d.name}` : `Hide ${d.name}`,
      );
      eye.title = isHidden ? "Show device" : "Hide device";
      eye.innerHTML = isHidden
        ? `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 3l18 18M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 9 4.5 10 7- .4 1-1.3 2.4-2.7 3.7M6.6 6.6C4.3 8 2.7 10.2 2 12c1 2.5 5 7 10 7 1.4 0 2.7-.3 3.9-.9"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></g></svg>`
        : `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12Z"/><circle cx="12" cy="12" r="3"/></g></svg>`;
      Object.assign(eye.style, {
        border: "none",
        background: "none",
        cursor: "pointer",
        color: isHidden ? "#9ca3af" : "#374151",
        padding: "4px",
        flex: "none",
      } satisfies Partial<CSSStyleDeclaration>);
      eye.addEventListener("click", () => {
        const cur = swimlaneDevicePrefs(eocId);
        const nextHidden = new Set(cur.hidden);
        if (nextHidden.has(d.deviceId)) nextHidden.delete(d.deviceId);
        else nextHidden.add(d.deviceId);
        persist({ order: cur.order, hidden: [...nextHidden] });
        rebuild();
      });
      row.appendChild(eye);
      list.appendChild(row);
    }
  };
  rebuild();

  new Sortable(list, {
    handle: ".device-panel-grip",
    animation: 120,
    // Recipe reference_sortablejs_chrome_drag_verification: fallback mode
    // gives a consistent styled ghost on every platform AND makes the drag
    // drivable by automation (native HTML5 DnD is neither).
    forceFallback: true,
    onEnd: () => {
      const order = [...list.children]
        .map((el) => (el as HTMLElement).dataset.deviceId)
        .filter((x): x is string => !!x);
      const cur = swimlaneDevicePrefs(eocId);
      persist({ order, hidden: cur.hidden });
    },
  });

  reset.addEventListener("click", () => {
    persist({ order: [], hidden: [] });
    rebuild();
  });

  // Dismiss on outside click or Escape — the panel is a popover, not a modal.
  const close = () => {
    pop.remove();
    document.removeEventListener("mousedown", onDown, true);
    document.removeEventListener("keydown", onKey, true);
  };
  const onDown = (e: MouseEvent) => {
    if (!pop.contains(e.target as Node) && e.target !== anchor) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("mousedown", onDown, true);
  document.addEventListener("keydown", onKey, true);

  document.body.appendChild(pop);
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}
